import React, { ReactElement, useEffect, useRef } from 'react';
import { Typography } from '@material-ui/core';
import { useElements } from '@stripe/react-stripe-js';

interface KlarnaPromoProps {
  amount: number;
  compact?: boolean;
}

const KlarnaPromo = ({
  amount,
  compact = false,
}: KlarnaPromoProps): ReactElement | null => {
  const elements = useElements();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const messagingElementRef = useRef<{
    mount: (el: HTMLElement | string) => void;
    destroy: () => void;
  } | null>(null);

  useEffect(() => {
    if (!elements || !containerRef.current || !amount || amount <= 0) {
      return undefined;
    }

    if (messagingElementRef.current) {
      messagingElementRef.current.destroy();
      messagingElementRef.current = null;
    }

    const paymentMethodMessagingElement = (
      elements as unknown as {
        create: (
          type: 'paymentMethodMessaging',
          options: {
            amount: number;
            currency: string;
            countryCode: string;
            paymentMethodTypes: string[];
          }
        ) => {
          mount: (el: HTMLElement | string) => void;
          destroy: () => void;
        };
      }
    ).create('paymentMethodMessaging', {
      amount: Math.round(amount * 100),
      currency: 'USD',
      countryCode: 'US',
      paymentMethodTypes: ['klarna'],
    });

    paymentMethodMessagingElement.mount(containerRef.current);
    messagingElementRef.current = paymentMethodMessagingElement;

    return () => {
      paymentMethodMessagingElement.destroy();
      messagingElementRef.current = null;
    };
  }, [amount, elements]);

  if (!amount || amount <= 0) {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: compact ? 'center' : 'flex-start',
        gap: 12,
        padding: compact ? '10px 12px' : '14px 16px',
        background: '#FFF4FB',
        border: '1px solid #F3D3E7',
        borderRadius: 12,
        marginTop: 16,
        marginLeft: 'auto',
        marginRight: 'auto',
        width: '100%',
        maxWidth: compact ? 360 : 420,
        boxSizing: 'border-box',
      }}
    >
      <div style={{ width: '100%' }}>
        <Typography
          style={{
            fontFamily: 'Inter',
            fontSize: compact ? 14 : 15,
            fontWeight: 700,
            color: '#111',
            marginBottom: 6,
          }}
        >
          Pay over time with Klarna
        </Typography>
        <div ref={containerRef} />
      </div>
    </div>
  );
};

KlarnaPromo.defaultProps = {
  compact: false,
};

export default KlarnaPromo;
